import { getUserGeoInfo } from "data/slices/login/api";
import {
  getInvitationDetailsFromHash,
  signupApi,
} from "data/slices/signup/api";
import {
  InvitationHashResponse,
  SignupResponse,
} from "data/slices/signup/interface";
import keys from "locale/keys";
import { isEmpty, isEqual, isUndefined } from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import { ROUTES } from "utils/constants";
import { saveTokens } from "utils/Utils";
import PURE from "widgets/prelogin/signup";

import { LoginContainerProps } from "../interface";

const SignupContainer = (props: LoginContainerProps) => {
  const { onShowSnackbarHandler } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSignupSuccess, setIsSignupSuccess] = useState(false);
  const [isAccountAlreadyExists, setIsAccountAlreadyExists] = useState(false);
  const [hashDetails, setHashDetails] = useState<InvitationHashResponse>({});

  useEffect(() => {
    if (
      !isUndefined(params.id) &&
      !isEmpty(params.id) &&
      !isEqual(params.id, ":id")
    ) {
      fetchDetailsFromHash(params.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  const fetchDetailsFromHash = async (id: string) => {
    setIsLoading(true);
    const details: InvitationHashResponse = await getInvitationDetailsFromHash(
      params.id as string,
    );

    setIsLoading(false);
    if (!isUndefined(details) && !isUndefined(details.response)) {
      setHashDetails(details);

      if (isEqual(details.response?.status, "UserRegistered")) {
        setIsAccountAlreadyExists(true);
      }
    }
  };

  const onSignupButtonClickedHandler = (
    firstName: string,
    lastName: string,
    email: string,
    password: string,
  ) => {
    getUserGeoDetails(firstName, lastName, email, password);
  };

  const getUserGeoDetails = async (
    firstName: string,
    lastName: string,
    email: string,
    password: string,
  ) => {
    setIsLoading(true);
    const userGeoInfo = await getUserGeoInfo();
    setIsLoading(false);

    signupUser(firstName, lastName, email, password, userGeoInfo?.ip);
  };

  const signupUser = async (
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    ip?: string,
  ) => {
    setIsLoading(true);
    const utmSource = searchParams.get("utm_source");
    const utmMedium = searchParams.get("utm_medium");
    const utmCampaign = searchParams.get("utm_campaign");
    const utmContent = searchParams.get("utm_content");
    const utmTerm = searchParams.get("utm_term");
    const signupResponse: SignupResponse = await signupApi({
      firstName,
      lastName,
      email,
      password,
      role: "User",
      ip: ip ?? "",
      ...(hashDetails?.response ? { hash: params.id } : {}),
      ...(utmSource ? { utmSource } : {}),
      ...(utmMedium ? { utmMedium } : {}),
      ...(utmCampaign ? { utmCampaign } : {}),
      ...(utmContent ? { utmContent } : {}),
      ...(utmTerm ? { utmTerm } : {}),
    });

    setIsLoading(false);
    if (isUndefined(signupResponse)) {
      onShowSnackbarHandler(t(keys.generic_error));
    } else if (!isEmpty(signupResponse.error)) {
      //todo, for now checking based on string. Should be based on a field in response
      if (isEqual(signupResponse.message, "email must be unique")) {
        setIsAccountAlreadyExists(true);
      } else {
        onShowSnackbarHandler(signupResponse.message);
      }
    } else {
      saveTokens(signupResponse.response);
      setIsSignupSuccess(true);
    }
  };

  const onLoginClickedHandler = () => {
    navigate(ROUTES.LOGIN);
  };

  return (
    <PURE
      onSignupHandler={onSignupButtonClickedHandler}
      isLoading={isLoading}
      isSignupSuccess={isSignupSuccess}
      onLoginClickedHandler={onLoginClickedHandler}
      isAccountAlreadyExists={isAccountAlreadyExists}
      details={hashDetails}
    />
  );
};

export default SignupContainer;
